import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../../components/common/PageHeader/PageHeader';
import authenticatedAxios from '../../authenticatedAxios';
import { baseurl } from '../../api';
import './Accounting.css';

const AccountingConfigurationDetail = () => {
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchConfiguration = async () => {
    try {
      setLoading(true);
      const response = await authenticatedAxios.get(`${baseurl}accounting/master-accounting-configuration/`);
      if (response?.data && response?.data?.data) {
        setConfig(response.data.data);
      } else {
        setConfig(null);
      }
    } catch (error) {
      console.error('Error fetching master accounting configuration:', error);
      if (error?.response?.data?.error_code === 1001) {
        navigate("/permission-denied");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfiguration();
  }, []);

  const renderValue = (val) => {
    if (val) {
      return <div className="accounting-view-box">{val}</div>;
    }
    return <div className="accounting-view-box accounting-view-empty">Not configured</div>;
  };

  if (loading) {
    return (
      <div className="whole-container">
        <PageHeader pageName="Accounting /" nestedpageName="Configurations" linkTo="/accounting/configuration" />
        <div className="accounting-config-container">
          <p className="common-text fts-14 ftw-4">Loading configuration...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="whole-container">
      <div className="top-heading">
        <PageHeader pageName="Accounting /" nestedpageName="Configurations" linkTo="/accounting/configuration" />
      </div>

      <div className="accounting-config-container">
        <div className="accounting-sub-header">
          Manage the default accounting configuration for your organization.
        </div>

        <div className="accounting-actions-header">
          <button
            type="button"
            className="accounting-btn-primary"
            onClick={() => navigate('/accounting/configuration/edit')}
          >
            Edit Configuration
          </button>
        </div>

        {/* DEFAULT JOURNALS */}
        <div className="accounting-card">
          <div className="accounting-card-title">DEFAULT JOURNALS</div>
          <div className="accounting-field-group">
            <label className="accounting-field-label">Default sales journal</label>
            {renderValue(config?.sales_journal_name)}
          </div>
          <div className="accounting-field-group">
            <label className="accounting-field-label">Purchase journal</label>
            {renderValue(config?.purchase_journal_name)}
          </div>
          <div className="accounting-field-group">
            <label className="accounting-field-label">Bank journal</label>
            {renderValue(config?.bank_journal_name)}
          </div>
          <div className="accounting-field-group">
            <label className="accounting-field-label">Cash journal</label>
            {renderValue(config?.cash_journal_name)}
          </div>
          <div className="accounting-field-group">
            <label className="accounting-field-label">Miscellaneous journal</label>
            {renderValue(config?.miscellaneous_journal_name)}
          </div>
        </div>

        {/* CURRENCY */}
        <div className="accounting-card">
          <div className="accounting-card-title">CURRENCY</div>
          <div className="accounting-field-group">
            <label className="accounting-field-label">Exchange gain account</label>
            {renderValue(config?.exchange_gain_account_name)}
          </div>
          <div className="accounting-field-group">
            <label className="accounting-field-label">Exchange loss account</label>
            {renderValue(config?.exchange_loss_account_name)}
          </div>
        </div>

        {/* INVENTORY */}
        <div className="accounting-card">
          <div className="accounting-card-title">INVENTORY</div>
          <div className="accounting-field-group">
            <label className="accounting-field-label">Stock valuation account</label>
            {renderValue(config?.stock_valuation_account_name)}
          </div>
          <div className="accounting-field-group">
            <label className="accounting-field-label">Opening stock adjustment account</label>
            {renderValue(config?.stock_adjustment_account_name)}
          </div>
          <div className="accounting-field-group">
            <label className="accounting-field-label">COGS Account</label>
            {renderValue(config?.cogs_account_name)}
          </div>
          <div className="accounting-field-group">
            <label className="accounting-field-label">GRNI Account</label>
            {renderValue(config?.grni_account_name)}
          </div>
        </div>

        {/* RECEIVABLES & PAYABLES */}
        <div className="accounting-card">
          <div className="accounting-card-title">RECEIVABLES &amp; PAYABLES</div>
          <div className="accounting-field-group">
            <label className="accounting-field-label">Default accounts receivable</label>
            {renderValue(config?.default_accounts_receivable_name)}
          </div>
          <div className="accounting-field-group">
            <label className="accounting-field-label">Default accounts payable</label>
            {renderValue(config?.default_accounts_payable_name)}
          </div>
          <div className="accounting-field-group">
            <label className="accounting-field-label">Customer advance account</label>
            {renderValue(config?.customer_advance_account_name)}
          </div>
          <div className="accounting-field-group">
            <label className="accounting-field-label">Vendor advance account</label>
            {renderValue(config?.vendor_advance_account_name)}
          </div>
        </div>

        {/* TAXES */}
        <div className="accounting-card">
          <div className="accounting-card-title">TAXES</div>
          <div className="accounting-field-group">
            <label className="accounting-field-label">Tax payable account</label>
            {renderValue(config?.tax_payable_account_name)}
          </div>
          <div className="accounting-field-group">
            <label className="accounting-field-label">Tax receivable account</label>
            {renderValue(config?.tax_receivable_account_name)}
          </div>
        </div>

        {/* OTHERS */}
        <div className="accounting-card">
          <div className="accounting-card-title">OTHERS</div>
          <div className="accounting-field-group">
            <label className="accounting-field-label">Suspense account</label>
            {renderValue(config?.suspense_account_name)}
          </div>
          <div className="accounting-field-group">
            <label className="accounting-field-label">Retained earnings</label>
            {renderValue(config?.retained_earnings_account_name)}
          </div>
          <div className="accounting-field-group">
            <label className="accounting-field-label">Rounding account</label>
            {renderValue(config?.rounding_account_name)}
          </div>
          <div className="accounting-field-group">
            <label className="accounting-field-label">Opening balance account</label>
            {renderValue(config?.opening_balance_equity_account_name)}
          </div>
          <div className="accounting-field-group">
            <label className="accounting-field-label">Discount expenses</label>
            {renderValue(config?.discount_expense_account_name)}
          </div>
          <div className="accounting-field-group">
            <label className="accounting-field-label">Discount income</label>
            {renderValue(config?.discount_income_account_name)}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountingConfigurationDetail;
